import { useCallback, useEffect, useState } from "react";
import { FolderOpen, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { saveOrDownload } from "@/lib/downloadFolder";
import DocumentFolderBox from "./DocumentFolderBox";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import DocumentFilterBar, { filterDocuments } from "./DocumentFilterBar";

const fmtEur = (v) => `€ ${Math.round(Math.abs(v)).toLocaleString("nl-NL")}`;
// Toast-tekst na "Kasstroom uit jaarverslag halen": bijgewerkte jaren + aansluiting per jaar.
export function liquiditySummary(d) {
  const parts = [];
  const reservesOnly = new Set(d.reserves_only_years || []);
  if (d.updated_years?.length) {
    const checks = (d.checks || []).map((c) => (reservesOnly.has(c.year)
      ? `${c.year} alleen reservemutaties (balans vorig jaareinde ontbreekt)`
      : Math.abs(c.diff) <= 1 ? `${c.year} sluit aan` : `${c.year} verschil ${c.diff > 0 ? "+" : "−"}${fmtEur(c.diff)}`));
    parts.push(`Kasstroom bijgewerkt voor ${d.updated_years.join(", ")}${checks.length ? ` · ${checks.join(" · ")}` : ""}.`);
  } else {
    parts.push("Geen kasstroom-regels bijgewerkt.");
  }
  if (d.missing_years?.length) parts.push(`Boekjaar ${d.missing_years.join(", ")} bestaat nog niet onder Voorgaande jaren.`);
  if (d.notes) parts.push(d.notes);
  return parts.join(" ");
}

// Tabblad "Documenten" op de clubpagina: Clubmap + Inbox (PREMIUM).
export default function ClubDocumentsPanel({ clubId, userId, onHistoricalsChanged }) {
  const [data, setData] = useState(null);
  const [gate, setGate] = useState(null);
  const [uploading, setUploading] = useState("");
  const [busyId, setBusyId] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  const [filters, setFilters] = useState({ q: "", type: "all", uploader: "all", source: "all" });
  const closePreview = useCallback(() => setPreviewDoc(null), []);

  const load = useCallback(async () => {
    try {
      setGate(null);
      setData((await api.get(`/clubs/${clubId}/documents`)).data);
    } catch (e) {
      if (e.response?.status === 402) setGate(formatApiError(e.response?.data?.detail));
      else toast.error(formatApiError(e.response?.data?.detail));
    }
  }, [clubId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (folder, files) => {
    setUploading(folder);
    for (const f of files) {
      const form = new FormData(); form.append("file", f); form.append("folder", folder);
      try {
        await api.post(`/clubs/${clubId}/documents`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        toast.success(`"${f.name}" geüpload.`);
      } catch (e) { toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail)}`); }
    }
    setUploading("");
    load();
  };

  const download = async (doc) => {
    setBusyId(doc.id);
    try {
      const res = await api.get(`/clubs/${clubId}/documents/${doc.id}/download`, { responseType: "blob", timeout: 180000 });
      await saveOrDownload(res.data, doc.filename, clubId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusyId("");
  };

  const remove = async (doc) => {
    if (!window.confirm(`"${doc.filename}" verwijderen?`)) return;
    try { await api.delete(`/clubs/${clubId}/documents/${doc.id}`); toast.success("Document verwijderd."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const relabel = async (doc, label) => {
    try { await api.patch(`/clubs/${clubId}/documents/${doc.id}`, { label }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const extractLiquidity = async (doc) => {
    if (!window.confirm(`Kasstroom-regels opnieuw uit "${doc.filename}" halen?\n\nDe AI leest de balans/het kasstroomoverzicht en overschrijft de afgeleide regels in de tabel Liquide middelen voor de boekjaren in dit jaarverslag (1 AI-aanroep). Exploitatie, subposten en reserves blijven staan.`)) return;
    setBusyId(doc.id);
    try {
      const { data } = await api.post(`/clubs/${clubId}/documents/${doc.id}/extract-liquidity`, {}, { timeout: 240000 });
      toast.success(liquiditySummary(data), { duration: 12000 });
      onHistoricalsChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusyId("");
  };

  if (gate) {
    return (
      <div className="border-2 border-foreground bg-card heavy-shadow p-6 flex items-start gap-4" data-testid="documents-gate">
        <div className="w-12 h-12 border-2 border-foreground bg-accent text-accent-foreground flex items-center justify-center shrink-0"><Lock className="w-6 h-6" /></div>
        <div>
          <div className="stat-label">Documenten</div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">Clubdocumenten · PREMIUM</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{gate}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="stat-label">Laden…</div>;

  const { documents, permissions, role, limits } = data;
  const isOwner = role === "owner";
  const canManageDoc = (d) => permissions[d.folder]?.manage || (d.folder === "inbox" && d.uploaded_by === userId);
  const common = { onUpload: upload, onDownload: download, onPreview: setPreviewDoc, onDelete: remove, onRelabel: relabel, onExtractLiquidity: extractLiquidity, busyId, canManageDoc, allowedExt: limits.allowed_ext };
  const visible = filterDocuments(documents, filters);
  const uploaders = [...new Map(documents.map((d) => [d.uploaded_by, { id: d.uploaded_by, name: d.uploaded_by_name || "—" }])).values()]
    .sort((a, b) => a.name.localeCompare(b.name));
  const filtering = visible.length !== documents.length;

  return (
    <div className="space-y-6" data-testid="documents-panel">
      <DocumentPreviewDialog doc={previewDoc} clubId={clubId} onClose={closePreview} onDownload={download} />
      <div className="flex items-start gap-3">
        <FolderOpen className="w-5 h-5 mt-1 shrink-0" />
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">Documenten van deze club</h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Iedereen met toegang tot de club kan alles downloaden; PDF's, afbeeldingen en tekstbestanden bekijk je direct in de app via het oog-icoon. Geïmporteerde bronbestanden (AI-PDF, Excel) worden automatisch in de Clubmap bewaard, zodat je altijd weet welke input is gebruikt.
          </p>
        </div>
      </div>
      {documents.length > 0 && (
        <DocumentFilterBar filters={filters} setFilters={setFilters} uploaders={uploaders} shown={visible.length} total={documents.length} />
      )}
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <DocumentFolderBox folder="club" title="Clubmap" perms={permissions.club} uploading={uploading === "club"} filtering={filtering}
          subtitle={isOwner || role === "editor" ? "Jij en de editors plaatsen hier bestanden voor het hele team." : "Bestanden van de eigenaar en editors — alleen downloaden."}
          docs={visible.filter((d) => d.folder === "club")} total={documents.filter((d) => d.folder === "club").length} {...common} />
        <DocumentFolderBox folder="inbox" title="Inbox" perms={permissions.inbox} uploading={uploading === "inbox"} filtering={filtering}
          subtitle={isOwner ? "Hier leveren teamleden bestanden bij jou aan. Jij downloadt en ruimt op." : "Lever hier bestanden aan voor de eigenaar van de club."}
          docs={visible.filter((d) => d.folder === "inbox")} total={documents.filter((d) => d.folder === "inbox").length} {...common} />
      </div>
      <div className="text-[11px] text-muted-foreground" data-testid="documents-usage">
        {documents.length} / {limits.max_docs} documenten gebruikt · toegestaan: {limits.allowed_ext.join(", ")}
      </div>
    </div>
  );
}
